/*
 * Remote-Web (docs/remote-web.md): Weboberfläche eines Geräts im Kundennetz
 * über den Field-Client in einem neuen Tab öffnen. Reine Funktionen, damit
 * sie ohne Browser testbar sind; den Aufruf macht openRemoteWeb in
 * remoteWebApi.ts.
 */

/** Ziel einer Remote-Web-Sitzung, wie der Server es annimmt. */
export interface WebTarget {
  schema: 'http' | 'https';
  port: number;
}

/** Was eine Zeile der Scan-Liste dafür mitbringt. */
export interface RemoteWebCandidate {
  ip: string;
  protocolHints: string[];
  isSelf: boolean;
  clientId?: string | null;
  anschluss?: string | null;
  direkt?: boolean | null;
}

/** Das Recht dafür. Eigene Risikoklasse wie remote.shell. */
export const REMOTE_WEB_PERMISSION = 'remote.web';

/** Darf der Benutzer Weboberflächen öffnen? */
export function canUseRemoteWeb(permissions: readonly string[] | undefined | null): boolean {
  return !!permissions && permissions.includes(REMOTE_WEB_PERMISSION);
}

/**
 * Welches Ziel der Knopf öffnet: https bevorzugt, sonst http. Die Hinweise
 * kommen vom Scanner (Port 443 gibt "https", Port 80 "http"). Null, wenn die
 * Zeile keine Weboberfläche zeigt.
 */
export function preferredWebTarget(hints: readonly string[]): WebTarget | null {
  const h = new Set(hints.map((x) => x.trim().toLowerCase()));
  if (h.has('https')) return { schema: 'https', port: 443 };
  if (h.has('http')) return { schema: 'http', port: 80 };
  return null;
}

/**
 * Der Knopf erscheint nur, wenn alles da ist: Recht, eine Weboberfläche, ein
 * Field-Client, über den das Gerät erreichbar ist, und nicht der Client
 * selbst.
 */
export function remoteWebAction(c: RemoteWebCandidate, permissions: readonly string[] | undefined | null):
  { target: WebTarget; clientId: string } | null {
  if (!canUseRemoteWeb(permissions) || c.isSelf || !c.clientId) return null;
  const target = preferredWebTarget(c.protocolHints);
  return target ? { target, clientId: c.clientId } : null;
}

/** Tooltip des Knopfs. */
export function remoteWebLabel(c: Pick<RemoteWebCandidate, 'ip'>, t: WebTarget): string {
  return `Im Browser öffnen (${t.schema}://${c.ip}) über den Field-Client`;
}

/** Vermerk, wenn das Gerät direkt an einem Anschluss des Clients hängt. */
export function directBadge(c: Pick<RemoteWebCandidate, 'direkt' | 'anschluss'>): string | null {
  if (c.direkt !== true || !c.anschluss) return null;
  return `am Client: ${c.anschluss}`;
}

/** Fehlertext zur Antwort des Servers beim Anlegen der Sitzung. */
export function openErrorText(status: number | null, reason?: string | null, message?: string | null): string {
  if (status === 403) return 'Dafür fehlt dir das Recht remote.web.';
  if (status === 404) return 'Der Field-Client gehört nicht zu dieser Anlage.';
  if (status === 409 || reason === 'no_device_key') {
    return message || 'Der Field-Client hat noch keinen hinterlegten Schlüssel.';
  }
  if (status === 400) return message || 'Das Ziel ist nicht zulässig.';
  if (status === 503) {
    return reason === 'no_server_key'
      ? 'Auf dem Server ist kein Verwaltungsschlüssel eingerichtet.'
      : 'Remote-Web ist auf dieser Instanz nicht eingeschaltet.';
  }
  return status ? `Öffnen fehlgeschlagen (${status}).` : 'Öffnen fehlgeschlagen.';
}
